import {
  ChatBubble,
  Close,
  DragIndicator,
  Fullscreen,
  FullscreenExit,
} from "@mui/icons-material";
import {
  Box,
  CardActions,
  CardHeader,
  IconButton,
  useTheme,
} from "@mui/material";
import { PimoReactComponent } from "@pimo/pimo-app-builder";
import dayjs from "dayjs";
import { useCallback, useEffect, useRef, useState } from "react";

import { Card } from "../../molecules/card/card";
import ChatInput from "./components/chat-input/chat-input";
import ChatbotMessage from "./components/chat-message/chat-message";
import Typing from "./components/typing/typing";
import { sendMessage } from "./utils/fetch-helper";

export type MessageButton = {
  id: string;
  label: string;
  value: string;
};

export type Message = {
  role: "assistant" | "user";
  content: string;
  time?: string;
  buttons?: MessageButton[];
  metadata?: {
    isButtonResponse?: boolean;
    buttonId?: string;
  };
};

export interface ChatbotProps {
  /** URL of the backend the chatbot connects to  */
  url: string;
  /** selected half year for which the chatbot data should be based on */
  halfYear?: string;
  /** chatbot header title */
  title?: string;
  /** show the chatbot */
  show?: boolean;
}

export const Chatbot: PimoReactComponent<ChatbotProps> = ({
  title = "Chatbot",
  show = false,
  halfYear,
  url,
}) => {
  const theme = useTheme();
  const scrollRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const resizeRef = useRef<HTMLDivElement>(null);
  const [messages, setMessages] = useState<Array<Message>>([
    {
      role: "assistant",
      content: "Hello! How can I help you?",
      time: dayjs().format("HH:mm"),
    },
  ]);
  const [isTyping, setIsTyping] = useState(false);
  const [isChatVisible, setIsChatVisible] = useState(false);
  const [chatSize, setChatSize] = useState({ width: 400, height: 500 });
  const [isResizing, setIsResizing] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);

  const onMessageSubmit = async (message: Message) => {
    setMessages([...messages, { ...message, time: dayjs().format("HH:mm") }]);
    setIsTyping(true);

    const responseMsg = await sendMessage(url, message, halfYear);
    setIsTyping(false);
    setMessages((prevMessages) => [
      ...prevMessages,
      { ...responseMsg, time: dayjs().format("HH:mm") },
    ]);
  };

  const onButtonClick = useCallback(
    async (button: MessageButton) => {
      const buttonMessage: Message = {
        role: "user",
        content: button.label,
        metadata: {
          isButtonResponse: true,
          buttonId: button.id,
        },
      };

      setMessages((prev) => [
        ...prev,
        { ...buttonMessage, time: dayjs().format("HH:mm") },
      ]);
      setIsTyping(true);

      const responseMsg = await sendMessage(
        url,
        {
          ...buttonMessage,
          content: button.value,
        },
        halfYear
      );
      setIsTyping(false);
      setMessages((prevMessages) => [
        ...prevMessages,
        { ...responseMsg, time: dayjs().format("HH:mm") },
      ]);
    },
    [url, halfYear]
  );

  const handleMaximizeToggle = useCallback(() => {
    if (isMaximized) {
      setChatSize({ width: 400, height: 500 });
      setIsMaximized(false);
    } else {
      const maxWidth = Math.min(800, window.innerWidth * 0.8);
      const maxHeight = Math.min(700, window.innerHeight * 0.8);
      setChatSize({ width: maxWidth, height: maxHeight });
      setIsMaximized(true);
    }
  }, [isMaximized]);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      setIsResizing(true);

      const startX = e.clientX;
      const startY = e.clientY;
      const startWidth = chatSize.width;
      const startHeight = chatSize.height;

      const handleMouseMove = (e: MouseEvent) => {
        const newWidth = Math.max(
          350,
          Math.min(800, startWidth + (startX - e.clientX))
        );
        const newHeight = Math.max(
          400,
          Math.min(700, startHeight + (startY - e.clientY))
        );

        setChatSize({ width: newWidth, height: newHeight });
        setIsMaximized(false);
      };

      const handleMouseUp = () => {
        setIsResizing(false);
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };

      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    },
    [chatSize]
  );

  useEffect(() => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop =
        messagesContainerRef.current.scrollHeight;
    }
  }, [messages, isTyping]);

  if (!show) {
    return <></>;
  }

  const headerHeight = 50;
  const inputAreaHeight = 70;

  return isChatVisible ? (
    <Card
      ref={resizeRef}
      data-testid="chatbot"
      sx={{
        bottom: 10,
        borderRadius: "8px 8px 0px 0px",
        boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.15)",
        display: "flex",
        flexDirection: "column",
        height: chatSize.height,
        padding: 0,
        position: "fixed",
        right: 25,
        width: chatSize.width,
        zIndex: 1000,
        transition: isResizing ? "none" : "all 0.2s ease",
        overflow: "hidden",
        backgroundColor: "#ffffff",
        border: "1px solid rgba(0, 0, 0, 0.12)",
        "@media print": {
          display: "none",
        },
      }}
    >
      <CardHeader
        title={title}
        action={
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IconButton
              onClick={handleMaximizeToggle}
              size="small"
              sx={{
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.04)",
                },
              }}
              title={isMaximized ? "Minimize chatbot" : "Maximize chatbot"}
            >
              {isMaximized ? (
                <FullscreenExit
                  sx={{ fontSize: 18, color: "rgba(0, 0, 0, 0.54)" }}
                />
              ) : (
                <Fullscreen
                  sx={{ fontSize: 18, color: "rgba(0, 0, 0, 0.54)" }}
                />
              )}
            </IconButton>

            <IconButton
              onMouseDown={handleMouseDown}
              size="small"
              sx={{
                cursor: "nw-resize",
                padding: "4px",
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.04)",
                },
              }}
              title="Drag to resize"
            >
              <DragIndicator
                sx={{
                  fontSize: 16,
                  color: "rgba(0, 0, 0, 0.54)",
                  transform: "rotate(45deg)",
                }}
              />
            </IconButton>

            <IconButton
              aria-label="close"
              size="small"
              onClick={() => {
                setIsChatVisible(false);
              }}
              sx={{
                "&:hover": {
                  backgroundColor: "rgba(244, 67, 54, 0.04)",
                  color: "#f44336",
                },
              }}
            >
              <Close />
            </IconButton>
          </Box>
        }
        sx={{
          borderBottom: "1px solid #D9D9D9",
          height: headerHeight,
          minHeight: headerHeight,
          maxHeight: headerHeight,
          padding: "16px 20px",
          flexShrink: 0,
          backgroundColor: theme.palette.secondary.main,
          "& .MuiCardHeader-title": {
            fontSize: "16px",
            fontWeight: 600,
            color: theme.palette.primary.main,
          },
        }}
      />

      <Box
        ref={messagesContainerRef}
        sx={{
          height: `calc(${chatSize.height}px - ${headerHeight}px - ${inputAreaHeight}px)`,
          overflowY: "auto",
          overflowX: "hidden",
          padding: "16px 20px",
          display: "flex",
          flexDirection: "column",
          gap: 2,
          backgroundColor: "#ffffff",
          "&::-webkit-scrollbar": {
            width: "6px",
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "rgba(0, 0, 0, 0.05)",
            borderRadius: "3px",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "rgba(0, 0, 0, 0.2)",
            borderRadius: "3px",
            "&:hover": {
              backgroundColor: "rgba(0, 0, 0, 0.3)",
            },
          },
        }}
      >
        {messages.map((message, index) => (
          <ChatbotMessage
            key={`${message.role}-${index}`}
            message={message}
            onButtonClick={onButtonClick}
            containerWidth={chatSize.width}
          />
        ))}

        {isTyping && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-start",
              width: "100%",
            }}
          >
            <Typing containerWidth={chatSize.width} />
          </Box>
        )}

        <div ref={scrollRef} />
      </Box>

      <CardActions
        sx={{
          height: inputAreaHeight,
          minHeight: inputAreaHeight,
          maxHeight: inputAreaHeight,
          borderTop: "1px solid #e0e0e0",
          padding: "16px 20px",
          flexShrink: 0,
          backgroundColor: "#ffffff",
          alignItems: "center",
          gap: 2,
        }}
      >
        <ChatInput onMessageSubmit={onMessageSubmit} />
      </CardActions>
    </Card>
  ) : (
    <IconButton
      data-testid="chatbot-bubble"
      color="primary"
      size="large"
      aria-label="open chat window"
      onClick={() => {
        setIsChatVisible(true);
      }}
      sx={{
        bottom: 30,
        right: 30,
        position: "fixed",
        width: 60,
        height: 60,
        backgroundColor: theme.palette.primary.main,
        color: "white",
        boxShadow: `0px 4px 20px ${theme.palette.primary.main}40`,
        zIndex: 999,
        transition: "all 0.3s ease",
        "&:hover": {
          backgroundColor: theme.palette.primary.main,
          transform: "scale(1.05)",
          boxShadow: `0px 6px 25px ${theme.palette.primary.main}60`,
        },
        "@media print": {
          display: "none",
        },
      }}
    >
      <ChatBubble sx={{ fontSize: 28 }} />
    </IconButton>
  );
};

export default Chatbot;
